/* eslint-disable no-console */
import React, { useState } from "react";
import { Drawer, message } from "antd";
import { useDispatch, useSelector } from "react-redux";

import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Button from "../../../Core/Components/CustomButton";

import { updateTrips } from "../../../Redux/Slices/trips.slice";
import { batterySwapSteps } from "./constants";

import RequestDetails from "./components/RequestDetails";
import AssignmentSuccess from "./components/AssignmentSuccess";
import BatteryAssignment from "./components/BatteryAssignment";
import RejectRequestModal from "../../Requests/RejectRequestModal";
import { updateRequest } from "../../../Redux/Slices/requests.slice";
import {
  assignBatterySosAgent,
  getBatterySosByTrip,
} from "../../../Redux/APIs/batterySosAPI";

const BatterySwapDrawer = ({
  visible,
  onCancel,
  isRental = false,
  tripId,
  rentalId,
}) => {
  const dispatch = useDispatch();

  const { batterySwapRequestModal, activeBatterySosRequest, currentTrip } =
    useSelector((state) => state.trips);
  const { currentRental } = useSelector((state) => state.rentals);
  const { allLocations } = useSelector((state) => state.locations);
  const { rejectModal } = useSelector((state) => state.requests);

  const step =
    batterySwapRequestModal?.data?.step || batterySwapSteps.REQUEST_CREATED;

  const [selectedHub, setSelectedHub] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedBatteries, setSelectedBatteries] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [batteriesList, setBatteriesList] = useState([]);

  React.useEffect(() => {
    if (visible) {
      const targetHubId = Number(
        activeBatterySosRequest?.hubId ||
          activeBatterySosRequest?.hub_id ||
          (isRental
            ? currentRental?.hubId ||
              currentRental?.hub_id ||
              currentRental?.original_batt_hub
            : currentTrip?.hubId || currentTrip?.hub_id) ||
          0,
      );

      if (targetHubId) {
        const found = allLocations.find(
          (loc) => Number(loc.id) === targetHubId,
        );
        if (found) {
          setSelectedHub({ id: found.id, value: found.name });
        } else {
          setSelectedHub({ id: targetHubId, value: "Hub Station" });
        }
      }
    } else {
      setSelectedHub(null);
      setSelectedAgent(null);
      setSelectedBatteries([]);
      setAgentsList([]);
      setBatteriesList([]);
    }
  }, [
    visible,
    activeBatterySosRequest,
    currentTrip,
    currentRental,
    allLocations,
    isRental,
  ]);

  const expectedCount =
    activeBatterySosRequest?.tripBatteryCount ||
    (isRental
      ? currentRental?.batteries?.length
      : currentTrip?.batteries?.length) ||
    0;

  const updateStep = (newStep) => {
    dispatch(
      updateTrips({
        key: "batterySwapRequestModal",
        value: {
          visible: true,
          data: {
            step: newStep,
          },
        },
      }),
    );
  };

  const activeId = isRental
    ? rentalId || currentRental?.id
    : tripId || currentTrip?.id;

  const handleAssignAccept = async () => {
    if (
      !selectedHub ||
      !selectedAgent ||
      selectedBatteries.length !== expectedCount
    ) {
      message.error(
        `Please select Hub, Technical Agent and exactly ${expectedCount} batteries.`,
      );
      return;
    }
    const reqId = activeBatterySosRequest?.id;
    try {
      await dispatch(
        assignBatterySosAgent({
          id: reqId,
          hubId: selectedHub.id,
          agentId: selectedAgent.id,
          assignedBatteryIds: selectedBatteries,
        }),
      ).unwrap();

      message.success("Agent assigned successfully");

      if (activeId) {
        dispatch(
          getBatterySosByTrip(
            isRental ? { rentalId: activeId } : { tripId: activeId },
          ),
        );
      }

      updateStep(batterySwapSteps.REQUEST_ACCEPTED_AGENT);
      setSelectedHub(null);
      setSelectedAgent(null);
      setSelectedBatteries([]);
      setAgentsList([]);
      setBatteriesList([]);
    } catch (err) {
      console.error("Error assigning SOS agent:", err);
      message.error("Failed to assign agent");
    }
  };

  const getCloseButtonWidth = () => {
    if (step === batterySwapSteps.REQUEST_CREATED) {
      return "batterySwap";
    }

    if (
      [
        batterySwapSteps.REQUEST_ACCEPTED_AGENT,
        batterySwapSteps.REQUEST_COMPLETED_AGENT,
      ].includes(step)
    ) {
      return "full";
    }

    return "batterySwapStep3";
  };

  const closeButtonWidth = getCloseButtonWidth();

  const renderContent = () => {
    switch (step) {
      case batterySwapSteps.REQUEST_CREATED:
        return <RequestDetails step={step} />;

      case batterySwapSteps.SELECT_HUB:
      case batterySwapSteps.SELECT_AGENT:
        return (
          <BatteryAssignment
            updateStep={updateStep}
            selectedHub={selectedHub}
            setSelectedHub={setSelectedHub}
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
            selectedBatteries={selectedBatteries}
            setSelectedBatteries={setSelectedBatteries}
            agentsList={agentsList}
            setAgentsList={setAgentsList}
            batteriesList={batteriesList}
            setBatteriesList={setBatteriesList}
            expectedCount={expectedCount}
          />
        );

      case batterySwapSteps.REQUEST_ACCEPTED_AGENT:
      case batterySwapSteps.REQUEST_COMPLETED_AGENT:
        return <AssignmentSuccess step={step} />;

      default:
        return null;
    }
  };

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      open={visible}
      onClose={onCancel}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
    >
      <div className="h-full flex flex-col">
        {/* Header */}

        <div
          className={`px-6 ${
            [
              batterySwapSteps.REQUEST_CREATED,
              batterySwapSteps.REQUEST_ACCEPTED_AGENT,
              batterySwapSteps.REQUEST_COMPLETED_AGENT,
            ].includes(step)
              ? ""
              : "mb-6"
          }`}
        >
          <div
            className={`flex items-center justify-between py-6 ${
              [
                batterySwapSteps.REQUEST_CREATED,
                batterySwapSteps.REQUEST_ACCEPTED_AGENT,
                batterySwapSteps.REQUEST_COMPLETED_AGENT,
              ].includes(step)
                ? ""
                : "border-b border-light-grey"
            }`}
          >
            <p className="font-poppins font-medium text-base leading-none">
              Request Details
            </p>

            <CloseIcon
              className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
              onClick={onCancel}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 pt-0">{renderContent()}</div>

        {/* Footer */}
        <DrawerFooter
          step={step}
          onClose={onCancel}
          onAccept={() => updateStep(batterySwapSteps.SELECT_AGENT)}
          onAssignAccept={handleAssignAccept}
          closeButtonWidth={closeButtonWidth}
          isRental={isRental}
          activeId={activeId}
        />
        <RejectRequestModal
          visible={rejectModal?.visible}
          onCancel={() => {
            dispatch(
              updateRequest({
                key: "rejectModal",
                value: {
                  visible: false,
                  type: "batterySwap",
                },
              }),
            );

            dispatch(
              updateRequest({
                key: "requestDetailsVisible",
                value: false,
              }),
            );
          }}
        />
      </div>
    </Drawer>
  );
};

const DrawerFooter = ({
  step,
  onClose,
  onAccept,
  onAssignAccept,
  closeButtonWidth,
  isRental,
  activeId,
}) => {
  const dispatch = useDispatch();

  const showInitialActions = step === batterySwapSteps.REQUEST_CREATED;

  const showAssignmentAction =
    step === batterySwapSteps.SELECT_AGENT ||
    step === batterySwapSteps.SELECT_HUB;

  return (
    <div className="p-6 flex justify-between">
      <Button
        onClick={onClose}
        width={closeButtonWidth}
        colors="batterySwapClose"
      >
        Close
      </Button>

      {showInitialActions && (
        <>
          <Button
            colors="alert"
            width="batterySwap"
            onClick={() => {
              dispatch(
                updateRequest({
                  key: "rejectModal",
                  value: {
                    visible: true,
                    type: "batterySwap",
                    isRental,
                    rentalId: isRental ? activeId : undefined,
                    tripId: !isRental ? activeId : undefined,
                  },
                }),
              );
            }}
          >
            Reject
          </Button>

          <Button type="primary" width="batterySwap" onClick={onAccept}>
            Accept
          </Button>
        </>
      )}

      {showAssignmentAction && (
        <Button
          type="primary"
          width="batterySwapStep3"
          onClick={onAssignAccept}
        >
          Assign & Accept
        </Button>
      )}
    </div>
  );
};

export default BatterySwapDrawer;
